/** Raw OpenWeather `/data/2.5/weather` payload — only the fields the app reads. */
export interface OpenWeatherResponse {
  weather: { id: number; main: string; description: string; icon: string }[];
  main: {
    temp: number;
    feels_like: number;
    pressure: number;
    humidity: number;
  };
  visibility?: number;
  wind?: { speed?: number; deg?: number; gust?: number };
  clouds?: { all?: number };
  rain?: { '1h'?: number };
  snow?: { '1h'?: number };
  sys: { country?: string; sunrise: number; sunset: number };
  name: string;
}

/**
 * View model for the topbar weather chip/popover. Foreign units are converted
 * to the units system's metric source units here at the API boundary
 * (wind m/s → km/h, visibility m → km) so templates can lean on `measure`.
 */
export interface CurrentWeather {
  conditionId: number;
  group: string;
  description: string;
  iconCode: string;
  city: string;
  country: string;
  tempC: number;
  feelsLikeC: number;
  humidityPct: number;
  pressureHpa: number;
  windKmh: number | null;
  windDeg: number | null;
  gustKmh: number | null;
  visibilityKm: number | null;
  cloudsPct: number | null;
  rainMm: number | null;
  snowMm: number | null;
  sunriseMs: number;
  sunsetMs: number;
}

const round1 = (v: number): number => Math.round(v * 10) / 10;
const msToKmh = (ms: number): number => round1(ms * 3.6);

export function toCurrentWeather(r: OpenWeatherResponse): CurrentWeather {
  const condition = r.weather[0];
  return {
    conditionId: condition?.id ?? 0,
    group: condition?.main ?? '',
    description: condition?.description ?? '',
    iconCode: condition?.icon ?? '03d',
    city: r.name,
    country: r.sys.country ?? '',
    tempC: round1(r.main.temp),
    feelsLikeC: round1(r.main.feels_like),
    humidityPct: r.main.humidity,
    pressureHpa: r.main.pressure,
    windKmh: r.wind?.speed != null ? msToKmh(r.wind.speed) : null,
    windDeg: r.wind?.deg ?? null,
    gustKmh: r.wind?.gust != null ? msToKmh(r.wind.gust) : null,
    visibilityKm: r.visibility != null ? round1(r.visibility / 1000) : null,
    cloudsPct: r.clouds?.all ?? null,
    rainMm: r.rain?.['1h'] ?? null,
    snowMm: r.snow?.['1h'] ?? null,
    sunriseMs: r.sys.sunrise * 1000,
    sunsetMs: r.sys.sunset * 1000,
  };
}
